import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { Input, InputAddon, InputIcon, Select } from '@components';
import BudgetTemplateAPICaller from '@services/api/budgetTemplate';
import DashboardAPICaller from '@services/api/dashboard';
import { DashboardFilters } from '@services/api/dashboard/calls';
import { BudgetTemplate } from 'interfaces/BudgetTemplate';
import { usePermissionContext } from '@contexts/PermissionProvider';
import SummaryComponent, {
  DashboardSummary,
} from './components/SummaryComponent';
import { monthsChipsEnum } from 'enums';
import { SimpleChart } from '@components/Chart/SimpleChart';
import CostCenterAPICaller from '@services/api/costCenter';
import { CostCenter } from 'interfaces/CostCenter';
import { groupOptions } from '@components/Input/Select';
import { useFilters } from '@hooks/useFilters';
import { useForm } from 'react-hook-form';
import { Category } from 'interfaces/Category';
import CategoryAPICaller from '@services/api/category';

type DashboardData = {
  name: string;
  month: string;
  budgetTotal: number;
  realizedTotal: number;
  percentage: number;
};

export default function DashboardPage() {
  const { register, watch, setValue } = useForm();
  const { filters, updateFilters: setFilters, clearFilters } = useFilters();

  const [incomeData, setIncomeData] = useState<Array<DashboardData>>([]);

  const [expenseData, setExpenseData] = useState<Array<DashboardData>>([]);

  const { hasPermission } = usePermissionContext();

  const [incomeSummary, setIncomeSummary] = useState<DashboardSummary>({
    budgetTotal: 0,
    realizedTotal: 0,
  } as DashboardSummary);

  const [expenseSummary, setExpenseSummary] = useState<DashboardSummary>({
    budgetTotal: 0,
    realizedTotal: 0,
  } as DashboardSummary);

  const [budgetTemplatesFilter, setBudgetTemplatesFilter] = useState<
    Array<BudgetTemplate>
  >([]);

  const [costCenters, setCostCenters] = useState<Array<CostCenter>>([]);
  const [categories, setCategories] = useState<
    Array<{ label: string; value: string; costCenterId: string }>
  >([]);

  useEffect(() => clearFilters(), [clearFilters]);

  const loadBudgetTemplatesFilter = useCallback(() => {
    if (hasPermission('budgetTemplates', 'read')) {
      BudgetTemplateAPICaller.list().then((values) => {
        setBudgetTemplatesFilter(values.result);

        if (filters.budgetTemplateId) {
          return;
        }

        if (values.result.length > 0) {
          setFilters({
            budgetTemplate: values.result[0],
            budgetTemplateId: values.result[0].id,
            budgetTemplateName: `${values.result[0].name}`,
          });
        }
      });
    }
  }, [hasPermission, filters, setFilters]);

  const loadCostCenters = useCallback(() => {
    if (hasPermission('costCenters', 'read')) {
      CostCenterAPICaller.listAll().then((values) => {
        values.result.unshift({
          id: 'clearOptions',
          name: 'TODOS',
        });
        setCostCenters(values.result);
      });
    }
  }, [hasPermission]);

  const loadCategories = useCallback(() => {
    if (hasPermission('categories', 'read')) {
      CategoryAPICaller.listAll({}).then((values) => {
        values.result.unshift({
          id: 'clearOptions',
          name: 'TODOS',
        });
        setCategories(
          values.result.map((category: Category) => {
            return {
              label: category.name,
              value: category.id,
              costCenterId: category.costCenterId,
            };
          })
        );
      });
    }
  }, [hasPermission]);

  const load = useCallback((filters: DashboardFilters) => {
    if (!filters.budgetTemplateId) {
      return;
    }

    try {
      DashboardAPICaller.list({
        ...filters,
        type: 'income',
      }).then((values) => {
        setIncomeData(
          values.map((item: DashboardData) => ({
            ...item,
            month: monthsChipsEnum
              .find((monthChip) => monthChip.key === item.month)
              ?.text.toLocaleUpperCase(),
          }))
        );

        const incomeSummary = values.reduce(
          (acc: DashboardSummary, item: DashboardData) => {
            return {
              budgetTotal: acc.budgetTotal + item.budgetTotal,
              realizedTotal: acc.realizedTotal + item.realizedTotal,
            };
          },
          {
            budgetTotal: 0,
            realizedTotal: 0,
          } as DashboardSummary
        );
        setIncomeSummary(incomeSummary);
      });

      DashboardAPICaller.list({
        ...filters,
        type: 'expense',
      }).then((values) => {
        setExpenseData(
          values.map((item: DashboardData) => ({
            ...item,
            month: monthsChipsEnum
              .find((monthChip) => monthChip.key === item.month)
              ?.text.toLocaleUpperCase(),
          }))
        );

        const expenseSummary = values.reduce(
          (acc: DashboardSummary, item: DashboardData) => {
            return {
              budgetTotal: acc.budgetTotal + item.budgetTotal,
              realizedTotal: acc.realizedTotal + item.realizedTotal,
            };
          },
          {
            budgetTotal: 0,
            realizedTotal: 0,
          } as DashboardSummary
        );
        setExpenseSummary(expenseSummary);
      });
    } catch (error) {
      toast.error('Erro ao carregar dados do dashboard');
    }
  }, []);

  useEffect(() => {
    loadBudgetTemplatesFilter();
  }, [loadBudgetTemplatesFilter]);

  useEffect(() => {
    if (filters.budgetTemplateId !== undefined) load(filters);
  }, [filters, load]);

  useEffect(() => {
    loadCostCenters();
  }, [loadCostCenters]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const costCenterIncome = costCenters.find(
    (cs) => cs.id === filters.costCenterIncomeId
  );
  const costCenterExpense = costCenters.find(
    (cs) => cs.id === filters.costCenterExpenseId
  );

  return (
    <>
      <div className="p-s-300">
        <div className="d-flex justify-between pt-s-300 pb-s-600">
          <h1 className="text-primary-30 font-weight-semibold">
            Financeiro Ateliware
          </h1>
          <Select
            className="w-100 max-width-253"
            value={{
              label: filters.budgetTemplate
                ? `${filters.budgetTemplateName} - ${filters.budgetTemplate?.year}`
                : '',
              value: filters.budgetTemplateId,
            }}
            groupValue={'Budget - ' + filters.budgetTemplate?.year}
            placeholder="Filtrar por modelo de budget"
            options={groupOptions(budgetTemplatesFilter, {
              optionLabel: 'name',
              optionValue: 'id',
              groupLabel: 'year',
              groupPrefix: 'Budget',
            })}
            onSelect={(budgetTemplate: any) => {
              setFilters({
                budgetTemplateId: budgetTemplate.value,
                budgetTemplateName: budgetTemplate.label,
                budgetTemplate: budgetTemplatesFilter.find(
                  (item) => item.id === budgetTemplate.value
                ),
              });
            }}
          />
        </div>

        <div className="border-style-solid border-width-hairline border-color-neutral-95 ">
          <div className="bg-neutral-95 pl-s-550 pr-s-550 pt-s-200 pb-s-200">
            <span className="font-s-250 font-weight-medium text-primary-30">
              Evolução mensal de receitas
            </span>
          </div>

          <div>
            <SummaryComponent
              name="Orçado"
              budgetTotal={incomeSummary.budgetTotal}
              realizedTotal={incomeSummary.realizedTotal}
            >
              <div className="row gap-x-1">
                <div className="col">
                  <Select
                    value={{
                      label: filters.costCenterIncomeId
                        ? costCenterIncome?.name
                        : 'Selecione um centro de custo',
                      value: filters.costCenterIncomeId,
                    }}
                    className="w-100 max-width-253"
                    placeholder="Selecione um centro de custo"
                    groupValue={costCenterIncome?.department as string}
                    options={groupOptions(costCenters, {
                      optionLabel: 'name',
                      optionValue: 'id',
                      groupLabel: 'department',
                    })}
                    isSearchable={false}
                    onSelect={(costCenterIncome: any) => {
                      if (costCenterIncome.value === 'clearOptions') {
                        setFilters({ costCenterIncomeId: undefined });
                        return;
                      }
                      setFilters({
                        costCenterIncomeId: costCenterIncome.value,
                      });
                    }}
                  />
                </div>
                <div className="col">
                  <Input
                    className="w-100 max-width-253"
                    type="search"
                    form={register('categoryIncome')}
                    placeholder="Pesquisar"
                    options={categories
                      .filter(
                        (category) =>
                          category.value === 'clearOptions' ||
                          (filters.costCenterIncomeId
                            ? category.costCenterId ===
                              filters.costCenterIncomeId
                            : true)
                      )
                      .map((category) => ({
                        label: category.label,
                        value: category.value,
                      }))}
                    optionFilter={watch('categoryIncome')}
                    onOptionChange={(categoryIncome: any) => {
                      if (categoryIncome === undefined) return;
                      if (categoryIncome.value === 'clearOptions') {
                        setFilters({ categoryIncomeId: undefined });
                        return;
                      }
                      setValue('categoryIncome', categoryIncome.label);
                      setFilters({ categoryIncomeId: categoryIncome.value });
                    }}
                    prefixes={
                      <InputAddon left>
                        <InputIcon>search</InputIcon>
                      </InputAddon>
                    }
                  />
                </div>
              </div>
            </SummaryComponent>

            <div
              style={{ maxWidth: '1400px' }}
              className="pl-s-550 pr-s-550 pb-s-400"
            >
              <div>
                <SimpleChart
                  height={400}
                  data={incomeData}
                  dataChart={[
                    {
                      key: 'budgetTotal',
                      legendLabel: 'Orçado',
                      color: '#00B694',
                      currency: true,
                    },
                    {
                      key: 'realizedTotal',
                      legendLabel: 'Realizado',
                      color: '#3F608E',
                      currency: true,
                    },
                    {
                      key: 'percentage',
                      legendLabel: 'Alcançado (%)',
                      color: '#000',
                    },
                  ]}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="border-style-solid border-width-hairline border-color-neutral-95 mt-s-500">
          <div className="bg-neutral-95 pl-s-550 pr-s-550 pt-s-200 pb-s-200">
            <span className="font-s-250 font-weight-medium text-primary-30">
              Evolução mensal de despesas
            </span>
          </div>

          <div style={{ padding: '20px' }}>
            <SummaryComponent
              name="Orçado"
              budgetTotal={expenseSummary.budgetTotal}
              realizedTotal={expenseSummary.realizedTotal}
            >
              <div className="row gap-x-1">
                <div className="col-sm-6">
                  <Select
                    value={{
                      label: filters.costCenterExpenseId
                        ? costCenterExpense?.name
                        : 'Selecione um centro de custo',
                      value: filters.costCenterExpenseId,
                    }}
                    className="w-100 max-width-253"
                    placeholder="Selecione um centro de custo"
                    groupValue={costCenterExpense?.department as string}
                    options={groupOptions(costCenters, {
                      optionLabel: 'name',
                      optionValue: 'id',
                      groupLabel: 'department',
                    })}
                    isSearchable={false}
                    onSelect={(costCenterExpense: any) => {
                      if (costCenterExpense.value === 'clearOptions') {
                        setFilters({ costCenterExpenseId: undefined });
                        return;
                      }
                      setFilters({
                        costCenterExpenseId: costCenterExpense.value,
                      });
                    }}
                  />
                </div>
                <div className="col-sm-6">
                  <Input
                    className="w-100 max-width-253"
                    type="search"
                    form={register('categoryExpense')}
                    placeholder="Pesquisar"
                    options={categories
                      .filter(
                        (category) =>
                          category.value === 'clearOptions' ||
                          (filters.costCenterExpenseId
                            ? category.costCenterId ===
                              filters.costCenterExpenseId
                            : true)
                      )
                      .map((category) => ({
                        label: category.label,
                        value: category.value,
                      }))}
                    optionFilter={watch('categoryExpense')}
                    onOptionChange={(categoryExpense: any) => {
                      if (categoryExpense === undefined) return;
                      if (categoryExpense.value === 'clearOptions') {
                        setFilters({ categoryExpenseId: undefined });
                        return;
                      }
                      setValue('categoryExpense', categoryExpense.label);
                      setFilters({ categoryExpenseId: categoryExpense.value });
                    }}
                    prefixes={
                      <InputAddon left>
                        <InputIcon>search</InputIcon>
                      </InputAddon>
                    }
                  />
                </div>
              </div>
            </SummaryComponent>
            <div
              style={{ maxWidth: '1400px' }}
              className="pl-s-550 pr-s-550 pb-s-400"
            >
              <div>
                <SimpleChart
                  height={400}
                  data={expenseData}
                  dataChart={[
                    {
                      key: 'budgetTotal',
                      legendLabel: 'Orçado',
                      color: '#F0263D',
                    },
                    {
                      key: 'realizedTotal',
                      legendLabel: 'Realizado',
                      color: '#3F608E',
                    },
                    {
                      key: 'percentage',
                      legendLabel: 'Alcançado (%)',
                      color: '#000',
                    },
                  ]}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
